import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Typography, Box, TextField, IconButton, Popover, Switch, Card, CardContent, Skeleton, Button, Checkbox } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { cardSx, timeFieldSx, deleteButtonSx, addButtonSx, switchSx, timeDividerSx, TrashIcon } from './schedule';
const daysOfWeek = [
    { ru: 'Понедельник', en: 'monday' },
    { ru: 'Вторник', en: 'tuesday' },
    { ru: 'Среда', en: 'wednesday' },
    { ru: 'Четверг', en: 'thursday' },
    { ru: 'Пятница', en: 'friday' },
    { ru: 'Суббота', en: 'saturday' },
    { ru: 'Воскресенье', en: 'sunday' }
];
const initializeEmptySchedule = () => {
    return daysOfWeek.reduce((acc, { en }) => {
        acc[en] = { isActive: false, intervals: [] };
        return acc;
    }, {});
};
// Стили для кнопки копирования
const copyButtonSx = {
    width: 32,
    height: 32,
    borderRadius: '6px',
    backgroundColor: 'primary.main',
    color: 'white',
    '&:hover': {
        backgroundColor: 'primary.dark'
    }
};
// Стили для меню копирования
const copyMenuSx = {
    p: 2,
    width: 180,
    borderRadius: '12px',
    border: '1px solid rgba(5, 4, 71, 0.15)',
    background: 'linear-gradient(0deg, rgba(5, 4, 71, 0.05) 0%, rgba(5, 4, 71, 0.05) 100%), #FFF',
};
// Стили для чекбокса
const checkboxSx = {
    padding: 0,
    width: 20,
    height: 20,
    '& .MuiSvgIcon-root': {
        fontSize: 20,
    },
    '&.Mui-checked': {
        color: '#050447',
    },
};
// Скелетон для карточки дня
const ScheduleCardSkeleton = () => (_jsx(Card, Object.assign({ sx: cardSx }, { children: _jsx(CardContent, Object.assign({ sx: { p: 2, '&:last-child': { pb: 2 } } }, { children: _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' } }, { children: [_jsxs(Box, { children: [_jsx(Skeleton, { variant: "text", width: 120, height: 28 }), _jsx(Skeleton, { variant: "text", width: 100, height: 20 })] }), _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: 0.5 } }, { children: [_jsx(Skeleton, { variant: "rounded", width: 60, height: 32, sx: { borderRadius: '16px' } }), _jsx(Skeleton, { variant: "rounded", width: 32, height: 32, sx: { borderRadius: '6px' } })] }))] })) })) })));
const ScheduleEdit = ({ schedule, onChange, isLoading = false }) => {
    const [internalSchedule, setInternalSchedule] = useState(initializeEmptySchedule());
    const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
    const [selectedDayIndex, setSelectedDayIndex] = useState(null);
    const [selectedCopyDays, setSelectedCopyDays] = useState(new Set());
    useEffect(() => {
        if (!schedule || Object.keys(schedule).length === 0) {
            // Просто инициализируем внутреннее состояние, не вызываем onChange
            // onChange вызывается только при реальных изменениях пользователем
            setInternalSchedule(initializeEmptySchedule());
        }
        else {
            const fullSchedule = Object.assign(Object.assign({}, initializeEmptySchedule()), schedule);
            setInternalSchedule(fullSchedule);
        }
    }, [schedule]);
    const handleDayToggle = (day) => {
        const newSchedule = Object.assign({}, internalSchedule);
        const wasActive = newSchedule[day].isActive;
        newSchedule[day] = Object.assign(Object.assign({}, newSchedule[day]), { isActive: !wasActive, intervals: !wasActive && newSchedule[day].intervals.length === 0
                ? [{ start: '09:00', end: '18:00' }]
                : newSchedule[day].intervals });
        setInternalSchedule(newSchedule);
        onChange(newSchedule);
    };
    const handleIntervalChange = (day, intervalIndex, field, value) => {
        const newSchedule = Object.assign({}, internalSchedule);
        newSchedule[day] = Object.assign(Object.assign({}, newSchedule[day]), { intervals: newSchedule[day].intervals.map((interval, idx) => idx === intervalIndex ? Object.assign(Object.assign({}, interval), { [field]: value }) : interval) });
        setInternalSchedule(newSchedule);
        onChange(newSchedule);
    };
    const addInterval = (day) => {
        const newSchedule = Object.assign({}, internalSchedule);
        newSchedule[day] = Object.assign(Object.assign({}, newSchedule[day]), { intervals: [...newSchedule[day].intervals, { start: '09:00', end: '18:00' }] });
        setInternalSchedule(newSchedule);
        onChange(newSchedule);
    };
    const removeInterval = (day, intervalIndex) => {
        const newSchedule = Object.assign({}, internalSchedule);
        newSchedule[day] = Object.assign(Object.assign({}, newSchedule[day]), { intervals: newSchedule[day].intervals.filter((_, idx) => idx !== intervalIndex) });
        setInternalSchedule(newSchedule);
        onChange(newSchedule);
    };
    const handleCopyClick = (event, index) => {
        setCopyMenuAnchor(event.currentTarget);
        setSelectedDayIndex(index);
        setSelectedCopyDays(new Set());
    };
    const handleCopyClose = () => {
        setCopyMenuAnchor(null);
        setSelectedDayIndex(null);
        setSelectedCopyDays(new Set());
    };
    const handleToggleCopyDay = (dayEn) => {
        const newSelected = new Set(selectedCopyDays);
        if (newSelected.has(dayEn)) {
            newSelected.delete(dayEn);
        }
        else {
            newSelected.add(dayEn);
        }
        setSelectedCopyDays(newSelected);
    };
    const handleToggleAllDays = () => {
        if (selectedDayIndex === null)
            return;
        const sourceDay = daysOfWeek[selectedDayIndex].en;
        const otherDays = daysOfWeek.filter((_, idx) => idx !== selectedDayIndex).map(d => d.en);
        if (selectedCopyDays.size === otherDays.length) {
            setSelectedCopyDays(new Set());
        }
        else {
            setSelectedCopyDays(new Set(otherDays));
        }
    };
    const handleApplyCopy = () => {
        if (selectedDayIndex !== null && selectedCopyDays.size > 0) {
            const sourceDay = daysOfWeek[selectedDayIndex].en;
            const newSchedule = Object.assign({}, internalSchedule);
            selectedCopyDays.forEach(targetDay => {
                newSchedule[targetDay] = {
                    isActive: true,
                    intervals: JSON.parse(JSON.stringify(internalSchedule[sourceDay].intervals))
                };
            });
            setInternalSchedule(newSchedule);
            onChange(newSchedule);
        }
        handleCopyClose();
    };
    // Показываем скелетоны при загрузке
    if (isLoading) {
        return (_jsx(Box, Object.assign({ sx: { display: 'flex', flexDirection: 'column', gap: 1 } }, { children: daysOfWeek.map(({ en }) => (_jsx(ScheduleCardSkeleton, {}, en))) })));
    }
    const allOtherDaysSelected = selectedDayIndex !== null &&
        selectedCopyDays.size === daysOfWeek.length - 1;
    return (_jsxs(Box, Object.assign({ sx: { display: 'flex', flexDirection: 'column', gap: 1 } }, { children: [daysOfWeek.map(({ ru, en }, index) => {
                const daySchedule = internalSchedule[en];
                const isActive = (daySchedule === null || daySchedule === void 0 ? void 0 : daySchedule.isActive) || false;
                const intervals = (daySchedule === null || daySchedule === void 0 ? void 0 : daySchedule.intervals) || [];
                return (_jsx(Card, Object.assign({ sx: cardSx }, { children: _jsxs(CardContent, Object.assign({ sx: { p: 2, '&:last-child': { pb: 2 } } }, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: isActive && intervals.length > 0 ? 1.5 : 0 } }, { children: [_jsxs(Box, { children: [_jsx(Typography, Object.assign({ sx: { fontWeight: 600, fontSize: '18px', color: '#050447' } }, { children: ru })), _jsx(Typography, Object.assign({ sx: { fontSize: '14px', color: 'rgba(5, 4, 71, 0.6)' } }, { children: isActive ? 'Бронь открыта' : 'Бронь закрыта' }))] }), _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: 0.5 } }, { children: [_jsx(Switch, { checked: isActive, onChange: () => handleDayToggle(en), sx: switchSx }), _jsx(IconButton, Object.assign({ onClick: (e) => handleCopyClick(e, index), sx: copyButtonSx, size: "small" }, { children: _jsx(ContentCopyIcon, { sx: { fontSize: 18 } }) }))] }))] })), isActive && (_jsxs(Box, Object.assign({ sx: { display: 'flex', flexDirection: 'column', gap: 1 } }, { children: [intervals.map((interval, intervalIndex) => (_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: 1 } }, { children: [_jsx(TextField, { type: "time", value: interval.start, onChange: (e) => handleIntervalChange(en, intervalIndex, 'start', e.target.value), sx: timeFieldSx, size: "small" }), _jsx(Box, { sx: timeDividerSx }), _jsx(TextField, { type: "time", value: interval.end, onChange: (e) => handleIntervalChange(en, intervalIndex, 'end', e.target.value), sx: timeFieldSx, size: "small" }), _jsx(IconButton, Object.assign({ onClick: () => removeInterval(en, intervalIndex), sx: deleteButtonSx, size: "small" }, { children: _jsx(TrashIcon, {}) })), intervalIndex === intervals.length - 1 && (_jsx(Box, Object.assign({ sx: { flex: 1, display: 'flex', justifyContent: 'flex-end' } }, { children: _jsx(IconButton, Object.assign({ onClick: () => addInterval(en), sx: addButtonSx, size: "small" }, { children: _jsx(AddIcon, { sx: { color: 'primary.main', fontSize: 20 } }) })) })))] }), intervalIndex))), intervals.length === 0 && (_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { flex: 1 } }, { children: "\u0414\u043E\u0441\u0442\u0443\u043F\u043D\u043E \u043A\u0440\u0443\u0433\u043B\u043E\u0441\u0443\u0442\u043E\u0447\u043D\u043E" })), _jsx(IconButton, Object.assign({ onClick: () => addInterval(en), sx: addButtonSx, size: "small" }, { children: _jsx(AddIcon, { sx: { color: 'primary.main', fontSize: 20 } }) }))] })))] })))] })) }), en));
            }), _jsx(Popover, Object.assign({ open: Boolean(copyMenuAnchor), anchorEl: copyMenuAnchor, onClose: handleCopyClose, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                }, slotProps: {
                    paper: { sx: copyMenuSx }
                } }, { children: _jsxs(Box, Object.assign({ sx: { display: 'flex', flexDirection: 'column', gap: 1 } }, { children: [_jsx(Typography, Object.assign({ sx: { fontWeight: 600, fontSize: '14px', color: '#050447', mb: 0.5 } }, { children: "\u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043D\u0430:" })), _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }, onClick: handleToggleAllDays }, { children: [_jsx(Checkbox, { checked: allOtherDaysSelected, indeterminate: selectedCopyDays.size > 0 && !allOtherDaysSelected, sx: checkboxSx }), _jsx(Typography, Object.assign({ sx: { fontSize: '14px', color: '#050447' } }, { children: "\u0412\u0441\u0435 \u0434\u043D\u0438" }))] })), daysOfWeek.map(({ ru, en }, index) => {
                            const isSourceDay = index === selectedDayIndex;
                            const isSelected = selectedCopyDays.has(en);
                            return (_jsxs(Box, Object.assign({ sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: isSourceDay ? 'default' : 'pointer',
                                    opacity: isSourceDay ? 0.4 : 1
                                }, onClick: () => !isSourceDay && handleToggleCopyDay(en) }, { children: [_jsx(Checkbox, { checked: isSelected, disabled: isSourceDay, sx: checkboxSx }), _jsx(Typography, Object.assign({ sx: {
                                            fontSize: '14px',
                                            color: isSourceDay ? 'rgba(5, 4, 71, 0.4)' : '#050447'
                                        } }, { children: ru }))] }), en));
                        }), _jsx(Button, Object.assign({ variant: "outlined", onClick: handleApplyCopy, disabled: selectedCopyDays.size === 0, sx: {
                                mt: 1,
                                height: 32,
                                borderRadius: '6px',
                                borderColor: '#050447',
                                color: '#050447',
                                fontWeight: 600,
                                fontSize: '14px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#050447',
                                    backgroundColor: 'rgba(5, 4, 71, 0.05)'
                                },
                                '&.Mui-disabled': {
                                    borderColor: 'rgba(5, 4, 71, 0.2)',
                                    color: 'rgba(5, 4, 71, 0.4)'
                                }
                            } }, { children: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" }))] })) }))] })));
};
export default ScheduleEdit;
